import axios from 'axios';
const X_THERAME_API_VERSION = 'X-Therame-API-Version';
// The office and caregiver headers and refusal codes as `@therame/shared` names
// them (#1822, #1841). Copied, not imported: the shared barrel carries
// class-validator and reflect-metadata, which the booking widget's bundle check
// refuses. The tests hold these to the shared values.
const X_THERAME_OFFICE_ID = 'x-therame-office-id';
const X_THERAME_CAREGIVER_ID = 'x-therame-caregiver-id';
const X_THERAME_CALL_SESSION = 'x-therame-call-session';
const OFFICE_NOT_AVAILABLE = 'OFFICE_NOT_AVAILABLE';
const OFFICE_SELECTION_REQUIRED = 'OFFICE_SELECTION_REQUIRED';
/**
 * Header that carries the signed Steuerberater share-link token on the public
 * (`requiresAuth: false`) route. A 401 on such a request means "this link is
 * revoked/expired" — NOT "the keycloak session expired" — so the global
 * `onUnauthorized` redirect must be skipped and the public view left to render
 * its own error banner.
 */
export const X_STEUERBERATER_TOKEN = 'x-steuerberater-token';
/**
 * Header that carries the short-lived embed session token on the public
 * (unauthenticated) booking routes. Like the Steuerberater token, a 401 here
 * means "this key/session is invalid" — the embed key was revoked or the
 * session lapsed — NOT that a keycloak session died, so the global
 * `onUnauthorized` redirect must be skipped and the widget left to render its
 * own boot-refused message.
 */
export const X_EMBED_SESSION = 'x-embed-session';
const requestCarriedPublicToken = (error) => {
    const headers = error.config?.headers;
    if (!headers)
        return false;
    const carries = (name) => {
        // Axios may normalise request headers into an AxiosHeaders instance.
        if (typeof headers.get === 'function') {
            return Boolean(headers.get(name));
        }
        return Boolean(headers[name]);
    };
    return carries(X_STEUERBERATER_TOKEN) || carries(X_EMBED_SESSION);
};
/** The backend's answer to an office that doesn't resolve, or to a missing one (#1822). */
export const isOfficeRefusal = (error) => {
    if (error.response?.status !== 403)
        return false;
    const code = error.response.data?.code;
    return code === OFFICE_NOT_AVAILABLE || code === OFFICE_SELECTION_REQUIRED;
};
/**
 * Creates a configured axios instance for the Therame API.
 * This factory allows multiple apps to share the same configuration pattern
 * while customizing behavior (e.g., error handling, auth callbacks).
 */
export function createApiClient(config) {
    const instance = axios.create({
        baseURL: config.baseURL,
        headers: {
            [X_THERAME_API_VERSION]: config.apiVersion ?? '1',
        },
    });
    // Request interceptor: attach the current bearer token, read fresh per
    // request. This makes the token impossible to leave stale — unlike a static
    // `defaults.headers` set once at bootstrap, which silently keeps sending an
    // expired token after the access-token lifespan elapses.
    if (config.getAuthToken) {
        instance.interceptors.request.use(async (requestConfig) => {
            const token = requestConfig.skipAuthToken ? undefined : await config.getAuthToken?.();
            if (token) {
                requestConfig.headers.Authorization = `Bearer ${token}`;
            }
            else {
                // No live token: send the request unauthenticated rather than falling
                // back to a stale `setAuthToken` header. A clean 401 is recoverable;
                // an expired token silently resurrected from bootstrap is not.
                requestConfig.headers.delete('Authorization');
            }
            return requestConfig;
        });
    }
    if (config.getCallSessionToken) {
        instance.interceptors.request.use((requestConfig) => {
            const callSession = config.getCallSessionToken?.();
            if (callSession) {
                requestConfig.headers.set(X_THERAME_CALL_SESSION, callSession);
                // The gateway lets a call token through only without a bearer (#1855).
                requestConfig.headers.delete('Authorization');
            }
            return requestConfig;
        });
    }
    if (config.getOfficeId) {
        instance.interceptors.request.use((requestConfig) => {
            const officeId = config.getOfficeId?.();
            if (officeId) {
                requestConfig.headers.set(X_THERAME_OFFICE_ID, officeId);
            }
            return requestConfig;
        });
    }
    if (config.getCaregiverId) {
        instance.interceptors.request.use((requestConfig) => {
            const caregiverId = config.getCaregiverId?.();
            if (caregiverId) {
                requestConfig.headers.set(X_THERAME_CAREGIVER_ID, caregiverId);
            }
            return requestConfig;
        });
    }
    // Response interceptor for error handling
    instance.interceptors.response.use((response) => response, (error) => {
        // Network error (no response)
        if (!error.response) {
            config.onNetworkError?.(error);
            return Promise.reject(error);
        }
        // 401 Unauthorized — but a 401 on a public route (Steuerberater share
        // link or embed session) means the credential is revoked/expired, not
        // that the keycloak session died. Let that view handle the error instead
        // of bouncing to keycloak.
        if (error.response.status === 401 && !requestCarriedPublicToken(error)) {
            config.onUnauthorized?.();
        }
        if (isOfficeRefusal(error)) {
            config.onOfficeRefused?.();
        }
        return Promise.reject(error);
    });
    const setAuthToken = (token) => {
        instance.defaults.headers.common.Authorization = `Bearer ${token}`;
    };
    const clearAuthToken = () => {
        delete instance.defaults.headers.common.Authorization;
    };
    return {
        instance,
        getOfficeId: () => config.getOfficeId?.() || null,
        getCaregiverId: () => config.getCaregiverId?.() || null,
        setAuthToken,
        clearAuthToken,
    };
}
// Singleton instance for use within query functions
// Apps should call initializeApiClient() before using queries
let apiClientInstance = null;
export function initializeApiClient(config) {
    apiClientInstance = createApiClient(config);
    return apiClientInstance;
}
export function getApiClient() {
    if (!apiClientInstance) {
        throw new Error('[@therame/api-client] API client not initialized. Call initializeApiClient() first.');
    }
    return apiClientInstance;
}
export function getAxiosInstance() {
    return getApiClient().instance;
}
//# sourceMappingURL=client.js.map